import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import { CalendarOutlined, RightOutlined } from '@ant-design/icons';
import type { DashboardFocusItem } from '../../../services/statisticsService';

const { Text, Title } = Typography;

const getPrimaryTagColor = (tag: string) => {
  if (tag.includes('逾期')) return 'error';
  if (tag.includes('临期') || tag.includes('今日')) return 'warning';
  if (tag.includes('新增')) return 'blue';
  return 'gold';
};

interface FocusSectionProps {
  title: string;
  items: DashboardFocusItem[];
  loading?: boolean;
  onItemClick: (item: DashboardFocusItem) => void;
  onViewMore: () => void;
}

const FocusSection: React.FC<FocusSectionProps> = ({ title, items, loading, onItemClick, onViewMore }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [paused, setPaused] = useState(false);

  const handleWheel: React.WheelEventHandler<HTMLDivElement> = (event) => {
    const container = containerRef.current;
    if (!container) return;

    const canScroll = container.scrollHeight > container.clientHeight;
    if (!canScroll) return;

    container.scrollTop += event.deltaY;
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || items.length <= 3 || paused) {
      return;
    }

    const timer = window.setInterval(() => {
      const step = 68;
      const maxScroll = container.scrollHeight - container.clientHeight;
      if (maxScroll <= 0) return;

      const nextTop = container.scrollTop + step >= maxScroll ? 0 : container.scrollTop + step;
      container.scrollTo({ top: nextTop, behavior: 'smooth' });
    }, 2600);

    return () => window.clearInterval(timer);
  }, [items.length, paused]);

  return (
    <Card
      size="small"
      styles={{ body: { padding: 16 } }}
      style={{ borderRadius: 18, background: 'rgba(236, 240, 243, 0.88)', border: '1px solid rgba(16, 27, 35, 0.08)' }}
      title={<span>{title}</span>}
      extra={<Space size={8}><Text type="secondary" style={{ fontSize: 12 }}>{items.length} 条</Text><Button type="link" size="small" onClick={onViewMore}>查看全部</Button></Space>}
    >
      {loading ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        </Space>
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无焦点数据" />
      ) : (
        <div
          ref={containerRef}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onWheel={handleWheel}
          style={{ maxHeight: 248, overflowY: 'auto', paddingRight: 4, overscrollBehavior: 'contain' }}
        >
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {items.map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                onClick={() => item.clickable && onItemClick(item)}
                style={{
                  borderRadius: 14,
                  padding: '12px 14px',
                  background: 'rgba(255,255,255,0.66)',
                  border: '1px solid rgba(16, 27, 35, 0.06)',
                  cursor: item.clickable ? 'pointer' : 'default',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  boxShadow: item.clickable ? '0 10px 24px rgba(16, 23, 29, 0.06)' : 'none'
                }}
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Text style={{ fontSize: 12, color: '#667986' }}>{item.projectName}</Text>
                      <div>
                        <Text strong ellipsis={{ tooltip: item.title }} style={{ color: '#10171d' }}>{item.title}</Text>
                      </div>
                    </div>
                    <Tag color={getPrimaryTagColor(item.primaryTag)} style={{ marginInlineEnd: 0, borderRadius: 999 }}>{item.primaryTag}</Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <Space size={8} wrap>
                      {item.secondaryTag ? <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>{item.secondaryTag}</Tag> : null}
                      <Text type="secondary" style={{ fontSize: 12 }}><CalendarOutlined /> {item.timeLabel}</Text>
                    </Space>
                    <RightOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                  </div>
                </Space>
              </div>
            ))}
          </Space>
        </div>
      )}
    </Card>
  );
};

interface ProjectFocusCardProps {
  additions: DashboardFocusItem[];
  rectifications: DashboardFocusItem[];
  loading?: boolean;
  onItemClick: (item: DashboardFocusItem) => void;
  onViewMore: (type: 'addition' | 'rectification') => void;
}

const ProjectFocusCard: React.FC<ProjectFocusCardProps> = ({ additions, rectifications, loading, onItemClick, onViewMore }) => {
  const additionItems = useMemo(() => additions.slice(0, 10), [additions]);
  const rectificationItems = useMemo(() => rectifications.slice(0, 10), [rectifications]);

  return (
    <Card
      variant="borderless"
      style={{
        borderRadius: 28,
        background: 'rgba(214, 221, 226, 0.92)',
        border: '1px solid rgba(16, 27, 35, 0.08)',
        boxShadow: '0 28px 60px rgba(46, 60, 70, 0.14)'
      }}
      styles={{ body: { padding: 20, height: '100%' } }}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Text style={{ color: '#667986', letterSpacing: '0.18em', fontSize: 12, fontWeight: 700 }}>PROJECT FOCUS</Text>
          <Title level={4} style={{ color: '#10171d', margin: '10px 0 0' }}>项目焦点</Title>
        </div>
        <FocusSection title="增项焦点" items={additionItems} loading={loading} onItemClick={onItemClick} onViewMore={() => onViewMore('addition')} />
        <FocusSection title="整改焦点" items={rectificationItems} loading={loading} onItemClick={onItemClick} onViewMore={() => onViewMore('rectification')} />
      </Space>
    </Card>
  );
};

export default ProjectFocusCard;
